import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'rsuite';
import {
  attachCandidates,
  commanderTax,
  cardFacesOf,
  moveTargetsFor,
  movePromptsForCommandCard,
  ZONE_LABELS,
  zoneOf,
} from '../../../shared/playtest';
import { displayOf, refusalText } from './playtestDisplay';
import { COMMAND_TO } from './PlaytestDnD';
import ReplacementPrompt from './ReplacementPrompt';
import { useEnsureCardDetails, usePlaytestSessions } from './PlaytestSessionProvider';
import { FRAME_BORDER, FRAME_RADIUS } from './playtestStyles';

const SPACING = { gap: 8, padding: 12 };
const HAND_TARGET = { to: 'hand', label: ZONE_LABELS.hand };

const styles = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.gap,
    padding: SPACING.padding,
    border: FRAME_BORDER,
    borderRadius: FRAME_RADIUS,
  },
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.gap },
};

/**
 * Details for one card: faces from GET /cards/:id, the tax for a command-zone
 * card, and move buttons. Moving a command-zone card out of the command zone
 * first asks whether it should return to the command zone instead. A battlefield
 * card also offers return to hand, attach to another battlefield card, and detach.
 */
const CardDetailBody = ({ collectionId, session, instanceId, onClose }) => {
  const { dispatchAction, requestCardDetail } = usePlaytestSessions();
  const state = session.engine;
  const instance = state.instances[instanceId];
  const cardId = instance?.cardId;
  const cardIds = useMemo(() => (cardId === undefined ? [] : [cardId]), [cardId]);
  useEnsureCardDetails(collectionId, session, cardIds);

  const [pendingTarget, setPendingTarget] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  if (!instance) return null;
  const { commandZoneCard } = instance;
  const zone = zoneOf(state, instanceId);
  const view = displayOf(session, cardId);
  const detail = session.details.get(cardId);
  const attached = state.attachments.some((entry) => entry.instanceId === instanceId);
  const hostIds = attachCandidates(state, instanceId);

  // Moves, attaches and detaches close the panel only when the engine accepted them. A refusal stays open and says why.
  const runAction = (action) => {
    const result = dispatchAction(collectionId, action);
    if (result === 'ok') {
      onClose();
      return;
    }
    setActionMessage(refusalText(result));
  };

  const moveTo = (to) => runAction({ type: 'move', instanceId, to });

  const chooseTarget = (target) => {
    if (movePromptsForCommandCard(state, instanceId, target.to)) {
      setPendingTarget(target);
      return;
    }
    moveTo(target.to);
  };

  const attachTo = (host) => runAction({ type: 'attach', instanceId, to: host });

  const detach = () => runAction({ type: 'detach', instanceId });

  const renderFaces = () => {
    if (!detail || detail.status === 'loading') {
      return <span data-testid="detail-loading">Loading card details…</span>;
    }
    if (detail.status === 'error') {
      return (
        <div data-testid="detail-error" style={styles.row}>
          <span>Could not load card details.</span>
          <Button size="xs" data-testid="detail-retry" onClick={() => requestCardDetail(collectionId, cardId)}>
            Retry
          </Button>
        </div>
      );
    }
    return cardFacesOf(detail.card).map((face, index) => (
      <section key={`${cardId}-face-${index}`} data-testid={`detail-face-${index}`}>
        <h4 data-testid="detail-name">{face.name}</h4>
        <div data-testid="detail-mana-cost">{face.mana_cost}</div>
        <div data-testid="detail-type-line">{face.type_line}</div>
        <p data-testid="detail-oracle-text">{face.oracle_text}</p>
      </section>
    ));
  };

  const renderBattlefieldActions = () => (
    <div data-testid="detail-battlefield-actions" style={styles.row}>
      <Button size="xs" data-testid="detail-return-to-hand" onClick={() => chooseTarget(HAND_TARGET)}>
        Return to hand
      </Button>
      <Button size="xs" data-testid="detail-attach-open" onClick={() => setAttachOpen((open) => !open)}>
        Attach to…
      </Button>
      {attached ? (
        <Button size="xs" data-testid="detail-detach" onClick={detach}>
          Detach
        </Button>
      ) : null}
    </div>
  );

  const renderAttachCandidates = () => (
    <div data-testid="detail-attach-candidates" style={styles.row}>
      {hostIds.map((hostId) => (
        <Button
          key={hostId}
          size="xs"
          data-testid={`detail-attach-${hostId}`}
          onClick={() => attachTo(hostId)}
        >
          {`Attach to ${displayOf(session, state.instances[hostId].cardId).name}`}
        </Button>
      ))}
    </div>
  );

  const renderMoveArea = () => {
    if (pendingTarget !== null) {
      return (
        <ReplacementPrompt
          name={view.name}
          destinationLabel={pendingTarget.label}
          onDestination={() => moveTo(pendingTarget.to)}
          onCommand={() => moveTo(COMMAND_TO)}
          onCancel={() => setPendingTarget(null)}
        />
      );
    }
    return (
      <div data-testid="detail-move-menu" style={styles.row}>
        {moveTargetsFor(state, instanceId).map((target) => (
          <Button
            key={target.to}
            size="xs"
            data-testid={`detail-move-${target.to}`}
            onClick={() => chooseTarget(target)}
          >
            {`Move to ${target.label}`}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="card-detail" style={styles.panel}>
      <div style={styles.row}>
        <strong>{view.name}</strong>
        <Button size="xs" data-testid="detail-close" onClick={onClose}>
          Close
        </Button>
      </div>
      {renderFaces()}
      {commandZoneCard ? (
        <div data-testid="detail-tax">{`Tax +${commanderTax(state, instanceId)}`}</div>
      ) : null}
      {zone === 'battlefield' ? renderBattlefieldActions() : null}
      {actionMessage ? <span data-testid="detail-action-error">{actionMessage}</span> : null}
      {zone === 'battlefield' && attachOpen ? renderAttachCandidates() : null}
      {renderMoveArea()}
    </div>
  );
};

/**
 * Keyed by instance, so a different card remounts the body. A pending prompt, a refusal or an open
 * attach list from the last card then cannot act on the new one.
 */
const CardDetailPanel = (props) => <CardDetailBody key={props.instanceId} {...props} />;

const cardDetailPropTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    details: PropTypes.instanceOf(Map).isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  instanceId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

CardDetailBody.propTypes = cardDetailPropTypes;
CardDetailPanel.propTypes = cardDetailPropTypes;

export default CardDetailPanel;
